import React from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import styled from 'styled-components';
import { MATCH_STATUSES, formatMatchStatusLabel } from '@/utils/matchStatus';

const FiltersContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 320px;

  @media (max-width: 640px) {
    max-width: 100%;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  pointer-events: none;
`;

const ClearButton = styled(Button)`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  padding: 4px;
  height: 24px;
  width: 24px;
`;

const StyledInput = styled(Input)`
  padding-left: 40px;
  padding-right: 32px;
`;

const FilterCount = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  margin-left: 0.5rem;
`;

interface MatchesFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusChange: (value: string) => void;
  resultCount?: number;
  totalCount?: number;
}

/**
 * Search and filter controls for the matches table
 */
export function MatchesFilters({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  resultCount,
  totalCount,
}: MatchesFiltersProps) {
  const hasActiveFilters = searchTerm.length > 0 || statusFilter !== 'all';

  return (
    <FiltersContainer>
      <SearchContainer>
        <SearchIcon size={18} />
        <StyledInput
          type="text"
          placeholder="Search by client, role, or candidate..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {searchTerm && (
          <ClearButton
            variant="ghost"
            size="sm"
            onClick={() => onSearchChange('')}
            aria-label="Clear search"
          >
            <X size={14} />
          </ClearButton>
        )}
      </SearchContainer>

      <Select value={statusFilter} onValueChange={onStatusChange}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="All Statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          {MATCH_STATUSES.map((status) => (
            <SelectItem key={status} value={status}>
              {formatMatchStatusLabel(status)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasActiveFilters && resultCount !== undefined && totalCount !== undefined && (
        <FilterCount>
          Showing {resultCount} of {totalCount}
        </FilterCount>
      )}
    </FiltersContainer>
  );
}

export default MatchesFilters;
